import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  requestAlarmGroupIntegrations,
  linkIntegrationToAlarmGroup as linkIntegrationAPI,
  unlinkIntegrationFromAlarmGroup as unlinkIntegrationAPI,
  ICreateAlarmGroupIntegrationDTO,
} from '_/api/alarmGroupIntegrations/alarmGroupIntegrations.api';
import { notification } from 'antd';

export const ALARM_GROUP_INTEGRATIONS_QUERY_KEY = (alarmGroupId: string): string[] => ['alarm-group-integrations', alarmGroupId];

/**
 * Fetch all integrations linked to an alarm group
 */
export const useAlarmGroupIntegrations = (alarmGroupId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: ALARM_GROUP_INTEGRATIONS_QUERY_KEY(alarmGroupId || ''),
    queryFn: () => requestAlarmGroupIntegrations(alarmGroupId!),
    enabled: !!alarmGroupId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Link an integration to an alarm group
 */
export const useLinkIntegrationToAlarmGroup = (
  alarmGroupId?: string,
): UseMutationResult<any, Error, ICreateAlarmGroupIntegrationDTO> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: ICreateAlarmGroupIntegrationDTO) => linkIntegrationAPI(dto),
    onSuccess: () => {
      // Invalidate alarm group integrations query to refetch the list
      if (alarmGroupId) {
        void queryClient.invalidateQueries({
          queryKey: ALARM_GROUP_INTEGRATIONS_QUERY_KEY(alarmGroupId),
        });
      }

      notification.success({
        message: 'Linked Alarm Group to Integration',
      });
    },
    onError: (error: any) => {
      console.error('Error linking integration to alarm group', error);
      notification.error({
        message: error.messages?.[0] || 'Error linking integration to alarm group',
      });
    },
  });
};

/**
 * Unlink an integration from an alarm group
 */
export const useUnlinkIntegrationFromAlarmGroup = (alarmGroupId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alarmGroupIntegrationId: string) => unlinkIntegrationAPI(alarmGroupIntegrationId),
    onSuccess: () => {
      // Invalidate alarm group integrations query to refetch the list
      if (alarmGroupId) {
        void queryClient.invalidateQueries({
          queryKey: ALARM_GROUP_INTEGRATIONS_QUERY_KEY(alarmGroupId),
        });
      }

      notification.success({
        message: 'Unlinked Alarm Group from Integration',
      });
    },
    onError: (error: any) => {
      console.error('Error unlinking integration from alarm group', error);
      notification.error({
        message: error.messages?.[0] || 'Error unlinking integration from alarm group',
      });
    },
  });
};
